import React, { FC, useEffect, useRef, useState } from "react";
import { useResponsiveVideoSource } from "../hooks";

interface Poster {
  webp: string;
  jpg: string;
}

interface Props {
  /** Class carrying the absolute-fill background styles, e.g. "homeHero--video". */
  className: string;
  desktopSrc: string;
  mobileSrc: string;
  desktopPoster: Poster;
  mobilePoster: Poster;
  minWidth?: number;
}

/**
 * Autoplaying background video that paints instantly.
 *
 * The <img> poster (a compressed still of the video's first frame) is the
 * LCP candidate: it sits in the initial HTML and is fetched with
 * fetchpriority=high, so first paint of the hero no longer waits for the
 * multi-megabyte mp4. The video downloads in the background and fades in
 * over the poster once playback actually starts. Browsers that block
 * autoplay (e.g. low-power mode) simply keep showing the poster.
 */
export const HeroVideo: FC<Props> = ({
  className,
  desktopSrc,
  mobileSrc,
  desktopPoster,
  mobilePoster,
  minWidth = 1024,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  useResponsiveVideoSource(videoRef, { desktopSrc, mobileSrc, minWidth });

  // On a static export the video can start playing before React hydrates,
  // in which case the `playing` event already fired with nobody listening.
  useEffect(() => {
    const video = videoRef.current;
    if (video && !video.paused && !video.ended && video.readyState >= 2) {
      setIsPlaying(true);
    }
  }, []);

  const desktopMedia = `(min-width: ${minWidth}px)`;

  return (
    <>
      <picture>
        <source
          media={desktopMedia}
          srcSet={desktopPoster.webp}
          type="image/webp"
        />
        <source media={desktopMedia} srcSet={desktopPoster.jpg} />
        <source srcSet={mobilePoster.webp} type="image/webp" />
        <img
          src={mobilePoster.jpg}
          alt=""
          className={className}
          decoding="async"
          // React 18.2 does not know the camelCase fetchPriority prop yet,
          // but passes unknown lowercase attributes straight through.
          {...({ fetchpriority: "high" } as any)}
        />
      </picture>
      <video
        ref={videoRef}
        muted
        autoPlay
        loop
        playsInline
        className={className}
        style={{ opacity: isPlaying ? 1 : 0, transition: "opacity 0.5s ease" }}
        onPlaying={() => setIsPlaying(true)}
      >
        <source src={desktopSrc} type="video/mp4" media={desktopMedia} />
        <source src={mobileSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </>
  );
};
